import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { bulkAddEpisodes } from '../redux/slices/episodeSlice';
import { X } from 'lucide-react';

const BulkEpisodeUpload = ({ seasonId, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useDispatch();

  // Acepta el archivo sin depender de file.type (a veces viene distinto a text/csv)
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
    } else {
      setFile(null);
      setError('Por favor, selecciona un archivo CSV válido.');
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'episodeNumber','name','runtime','description','poster','srtUrl','videoUrl','fileSize','mimeType'
    ].join(',') + '\n';

    const sampleRow = [
      '1',
      '"Episodio 1"',
      '24',
      '"Descripción del episodio"',
      '"https://ejemplo.com/poster.jpg"',
      '"https://ejemplo.com/subtitulos.srt"',
      '"https://ejemplo.com/video.mp4"',
      '104857600',
      '"video/mp4"'
    ].join(',') + '\n';

    const blob = new Blob([headers + sampleRow], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_episodios.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Parser robusto: autodetecta ; o , y soporta comillas/saltos de línea/BOM
  const parseCSV = async (file) => {
    const raw = await file.text();
    const data = raw.replace(/^\uFEFF/, ''); // quita BOM si existe

    // Detectar delimitador escaneando la primera fila real (fuera de comillas)
    const detectDelimiter = (s) => {
      let inQ = false, comma = 0, semi = 0;
      for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '"') {
          if (inQ && s[i + 1] === '"') { i++; continue; }
          inQ = !inQ; continue;
        }
        if (!inQ && (ch === '\n' || ch === '\r')) break;
        if (!inQ && ch === ',') comma++;
        if (!inQ && ch === ';') semi++;
      }
      return semi > comma ? ';' : ',';
    };

    const delimiter = detectDelimiter(data);

    // Parseo carácter por carácter (soporta newlines dentro de comillas)
    const rows = [];
    let field = '';
    let row = [];
    let inQuotes = false;

    for (let i = 0; i < data.length; i++) {
      const ch = data[i];

      if (ch === '"') {
        if (inQuotes && data[i + 1] === '"') { field += '"'; i++; continue; } // "" => "
        inQuotes = !inQuotes;
        continue;
      }

      if (!inQuotes && ch === delimiter) {
        row.push(field);
        field = '';
        continue;
      }

      if (!inQuotes && (ch === '\n' || ch === '\r')) {
        if (ch === '\r' && data[i + 1] === '\n') i++; // CRLF
        row.push(field);
        field = '';
        if (row.some(c => c.trim() !== '')) rows.push(row);
        row = [];
        continue;
      }

      field += ch;
    }

    // Última fila
    row.push(field);
    if (row.some(c => c.trim() !== '')) rows.push(row);

    if (rows.length === 0) return [];

    const clean = (v) => {
      let s = String(v ?? '').trim();
      if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1);
      return s;
    };

    const headers = rows[0].map(clean);

    // Requeridas
    const requiredHeaders = ['episodeNumber','name','runtime','videoUrl','srtUrl','fileSize','mimeType'];
    const missing = requiredHeaders.filter(h => !headers.includes(h));
    if (missing.length) {
      throw new Error(`El CSV no contiene todas las columnas requeridas. Falta: ${missing.join(', ')}`);
    }

    const toInt = (x) => {
      const n = parseInt(String(x ?? '').trim(), 10);
      return Number.isFinite(n) ? n : NaN;
    };

    // Construir objetos normalizados
    const episodes = [];
    for (let r = 1; r < rows.length; r++) {
      const cols = rows[r].map(clean);
      const rowObj = {};
      headers.forEach((h, i) => { rowObj[h] = cols[i] ?? ''; });

      let runtime = toInt(rowObj.runtime);
      if (Number.isFinite(runtime) && runtime >= 120) runtime = Math.round(runtime / 60); // segundos -> minutos

      let fileSize = toInt(rowObj.fileSize);
      if (!Number.isFinite(fileSize) || fileSize < 0) fileSize = 0;

      let mimeType = rowObj.mimeType || '';
      if (!mimeType && rowObj.videoUrl) {
        const v = String(rowObj.videoUrl).toLowerCase();
        if (v.endsWith('.mp4')) mimeType = 'video/mp4';
        else if (v.endsWith('.m3u8')) mimeType = 'application/x-mpegURL';
      }

      episodes.push({
        episodeNumber: toInt(rowObj.episodeNumber),
        name: rowObj.name || '',
        runtime,
        description: rowObj.description || '',
        poster: rowObj.poster || '',
        srtUrl: rowObj.srtUrl || '',
        videoUrl: rowObj.videoUrl || '',
        fileSize,
        mimeType,
      });
    }

    return episodes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Por favor, selecciona un archivo CSV.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const episodes = await parseCSV(file);

      if (episodes.length === 0) {
        throw new Error('No se encontraron datos de episodios en el archivo.');
      }

      // Validación requerida
      const required = ['episodeNumber','name','runtime','videoUrl','srtUrl','fileSize','mimeType'];
      const hasInvalid = episodes.some(ep =>
        required.some(f => {
          const v = ep[f];
          if (v === undefined || v === null || v === '') return true;
          if (['episodeNumber','runtime','fileSize'].includes(f)) return !Number.isFinite(Number(v));
          return false;
        })
      );

      if (hasInvalid) {
        throw new Error('Algunos episodios tienen campos obligatorios faltantes o inválidos.');
      }

      await dispatch(
        bulkAddEpisodes({
          seasonId,
          episodesData: episodes.map(ep => ({
            ...ep,
            title: ep.name,
            seasonId,
          })),
        })
      ).unwrap();

      alert(`${episodes.length} episodios cargados exitosamente`);
      onSuccess?.(episodes);
      onClose();
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : null);
      setError('Error: ' + (serverMsg || err?.message || 'Error desconocido'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-white">Importación de Episodios para Temporada</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-800">
            <X className="h-6 w-6 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-4">
            <p className="text-sm text-gray-300 mb-2">El archivo CSV debe incluir las siguientes columnas:</p>
            <div className="bg-gray-700 p-3 rounded-md mb-4">
              <ul className="list-disc pl-5 text-gray-300 text-xs mb-3">
                <li><b>episodeNumber</b>: número del episodio (entero).</li>
                <li><b>name</b>: título del episodio.</li>
                <li><b>runtime</b>: duración en minutos (entero).</li>
                <li><b>videoUrl</b>: URL al video del episodio.</li>
                <li><b>srtUrl</b>: URL a los subtítulos del episodio.</li>
                <li><b>fileSize</b>: tamaño del archivo en bytes (entero).</li>
                <li><b>mimeType</b>: tipo MIME del video (ej. "video/mp4").</li>
              </ul>

              <h5 className="text-xs text-white font-semibold mt-3">Campos opcionales:</h5>
              <ul className="list-disc pl-5 text-gray-300 text-xs mb-3">
                <li><b>description</b>: descripción o sinopsis del episodio.</li>
                <li><b>poster</b>: URL a la imagen de portada del episodio.</li>
              </ul>

              <h5 className="text-xs text-white font-semibold mt-3">Ejemplo:</h5>
              <code className="text-xs text-green-300 block mt-1 overflow-x-auto whitespace-nowrap">
                episodeNumber,name,runtime,description,poster,srtUrl,videoUrl,fileSize,mimeType<br/>
                1,"Primer Episodio",24,"Descripción del episodio","https://ejemplo.com/poster.jpg","https://ejemplo.com/subs.srt","https://ejemplo.com/video.mp4",104857600,"video/mp4"<br/>
                2,"Segundo Episodio",22,"Otra descripción",,"https://ejemplo.com/subs2.srt","https://ejemplo.com/video2.mp4",94371840,"video/mp4"
              </code>
              <p className="text-[11px] text-gray-300 mt-2">
                * Acepta CSV separados por <b>coma</b> o <b>punto y coma</b>, con comillas y saltos de línea dentro de campos.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">Archivo CSV</label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
            {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          </div>

          <div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Descargar plantilla CSV
            </button>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !file}
              className={`px-4 py-2 rounded-md text-white ${
                isLoading || !file ? 'bg-blue-500 opacity-50 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading ? 'Cargando...' : 'Cargar Episodios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEpisodeUpload;
